/**
 * spaceEnemySystem.ts
 * Spawner, update, and projectile-vs-enemy collision for World 2.
 *
 * Sibling to `asteroidSystem.ts`. Owns the active-enemy lifecycle on
 * `spaceState.enemies`; per-kind pools and the kind→spec/release mapping
 * live in `spaceEnemyPools.ts` so this file stays under the AGENTS.md
 * 300-line file limit as more enemy kinds land.
 *
 * Responsibilities:
 *   1. `resetSpaceEnemies()` / `updateSpaceEnemySpawner()` — Poisson-ish
 *      spawner that introduces space enemies at the right edge of the
 *      camera window. A single tick roll decides whether to spawn, then
 *      a second RNG call walks the cumulative weight ladder to pick a
 *      kind.
 *   2. `updateSpaceEnemies()` — integrates motion, reaps dead instances
 *      back to their pools via `releaseEnemy()`.
 *   3. `resolveShipProjectileEnemyCollisions()` — pairwise AABB tests
 *      between live ship projectiles and live enemies.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceEnemySystem
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { spawnExplosion } from './explosionVfx';
import { maybeSpawnSpaceLoot } from './spaceLootSystem';
import {
  SPACE_ENEMY_MAX_ACTIVE,
  SPACE_ENEMY_SPAWN_INTERVAL_S,
  SPACE_ENEMY_SPAWN_X_OFFSET,
  SPACE_ENEMY_SPAWN_Y_MIN,
  SPACE_ENEMY_SPAWN_Y_MAX_OFFSET,
  SPACE_ENEMY_FIGHTER_WEIGHT,
  SPACE_ENEMY_SATELLITE_WEIGHT,
  type SpaceEnemyKind,
} from '../../constants/spaceEnemyConstants';
import { SPAWN_SPEC, releaseEnemy } from './spaceEnemyPools';
import { updateFighterDroneLasers } from './fighterDroneFire';
import { updateBomberOrdnance } from './bomberDroneDrop';

// Re-export pool-introspection helpers so existing test imports keep working.
export {
  _fighterPoolAvailable,
  _bomberPoolAvailable,
  _satellitePoolAvailable,
} from './spaceEnemyPools';

/** Reusable empty level for enemy updates — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Injectable RNG so tests can pin spawn behaviour. Defaults to `Math.random`.
 * Production code MUST NOT call `setSpaceEnemyRng` outside of tests.
 *
 * @internal
 */
let rng: () => number = Math.random;

/**
 * Replace the spawner's RNG. Test-only seam.
 *
 * @param fn - Zero-arg function returning a number in `[0, 1)`.
 * @internal
 */
export function setSpaceEnemyRng(fn: () => number): void {
  rng = fn;
}

/**
 * Restore the spawner's RNG to `Math.random`. Call from test teardown so
 * pinned sequences do not leak across suites.
 *
 * @internal
 */
export function resetSpaceEnemyRng(): void {
  rng = Math.random;
}

/**
 * Release every active enemy back to its pool and clear the active list.
 * Called from `SpaceScene.init()` and `SpaceScene.destroy()` so enemies
 * never bleed across sessions.
 *
 * Owns the `spaceState.enemies` lifecycle. Like `resetAsteroids`, this
 * deliberately does NOT touch `rng` so a test-pinned RNG stays in place
 * across production-style reset paths.
 */
export function resetSpaceEnemies(): void {
  for (const e of spaceState.enemies) {
    releaseEnemy(e);
  }
  spaceState.enemies = [];
}

/**
 * Roll one spawn tick. Poisson-like: each fixed tick has probability
 * `dt / SPACE_ENEMY_SPAWN_INTERVAL_S` of producing one enemy. On a hit, a
 * second RNG call walks the cumulative weight ladder
 * (`fighter → satellite → bomber`) to pick the kind. Order is fixed so
 * the FighterDrone slice's `rng=0` pin still picks a fighter and the
 * BomberDrone slice's `rng=0.999` pin still picks a bomber.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateSpaceEnemySpawner(dt: number): void {
  if (spaceState.enemies.length >= SPACE_ENEMY_MAX_ACTIVE) return;

  const p = dt / SPACE_ENEMY_SPAWN_INTERVAL_S;
  if (rng() >= p) return;

  const kindRoll = rng();
  let kind: SpaceEnemyKind;
  if (kindRoll < SPACE_ENEMY_FIGHTER_WEIGHT) {
    kind = 'fighter';
  } else if (kindRoll < SPACE_ENEMY_FIGHTER_WEIGHT + SPACE_ENEMY_SATELLITE_WEIGHT) {
    kind = 'satellite';
  } else {
    kind = 'bomber';
  }

  // DECISION: Per-kind spawn lookup centralises height + Y-floor + pool
  // acquisition in `spaceEnemyPools.SPAWN_SPEC`. Each entry owns its full
  // vertical reach (hitbox + any wobble amplitude) and any extra inset
  // from `SPACE_ENEMY_SPAWN_Y_MIN`. New kinds add one row there.
  const spec = SPAWN_SPEC[kind];
  const drone = spec.acquire();
  if (drone === null) return; // Pool exhausted — drop the spawn silently.

  const spawnX = spaceState.cameraX + VIEWPORT_WIDTH + SPACE_ENEMY_SPAWN_X_OFFSET;
  const yMin = SPACE_ENEMY_SPAWN_Y_MIN + spec.yMinInset;
  const yMax = VIEWPORT_HEIGHT - spec.verticalReach - SPACE_ENEMY_SPAWN_Y_MAX_OFFSET;
  const yRange = Math.max(0, yMax - yMin);
  const spawnY = yMin + Math.floor(rng() * (yRange + 1));

  drone.spawn(spawnX, spawnY);
  spaceState.enemies.push(drone);
}

/**
 * Update all live enemies and reap dead ones back into their pools.
 * Also ticks the FighterDrone laser pool and the BomberDrone
 * cluster-bomb + shrapnel pools — those weapon pools are owned by
 * `fighterDroneFire` / `bomberDroneDrop` respectively, but they
 * belong to the enemy ecosystem at the step-pipeline level so the
 * orchestrator (`spaceStep`) sees a single "enemies" call.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateSpaceEnemies(dt: number): void {
  for (let i = spaceState.enemies.length - 1; i >= 0; i--) {
    const e = spaceState.enemies[i];
    e.update(dt, EMPTY_SPACE_LEVEL);
    if (e.dead) {
      releaseEnemy(e);
      spaceState.enemies.splice(i, 1);
    }
  }
  updateFighterDroneLasers(dt);
  updateBomberOrdnance(dt);
}

/**
 * Pairwise AABB collision between live ship projectiles and live enemies.
 *
 * Each hit deals 1 damage and is consumed by the projectile (`dead = true`,
 * `active = false`); `updateShipProjectiles` owns the splice + pool-release
 * on the next tick. Whether the enemy dies depends on its remaining HP
 * — fighters die in one hit (1 HP), bombers take two (2 HP), satellites
 * take three (3 HP).
 *
 * Off-screen guard: enemies whose LEFT edge is past the right edge of the
 * viewport are skipped (mirrors `resolveShipProjectileAsteroidCollisions`),
 * so an enemy still in the spawn margin can't absorb a projectile the
 * player never sees connect.
 *
 * Complexity: O(P*E). Caps of 20 projectiles × 12 enemies = 240 AABB tests
 * max per tick, well under the engine's 1 ms budget.
 */
export function resolveShipProjectileEnemyCollisions(): void {
  const projectiles = spaceState.projectiles;
  const enemies = spaceState.enemies;
  const viewportRight = spaceState.cameraX + VIEWPORT_WIDTH;
  for (let i = 0; i < projectiles.length; i++) {
    const p = projectiles[i];
    if (p.dead) continue;
    const pRight = p.x + p.width;
    const pBottom = p.y + p.height;
    for (let j = 0; j < enemies.length; j++) {
      const e = enemies[j];
      if (e.dead) continue;
      // Visibility gate — skip enemies still entirely past the viewport.
      if (e.x >= viewportRight) continue;
      // Inlined AABB overlap to avoid getHitbox allocation in the hot path.
      if (
        pRight <= e.x ||
        p.x >= e.x + e.width ||
        pBottom <= e.y ||
        p.y >= e.y + e.height
      ) {
        continue;
      }
      // DECISION: `takeDamage` returns `true` on the kill frame. Spawn
      // the burst VFX at the enemy's centre on that frame so the
      // explosion visually replaces the dying enemy. Saturated VFX pool
      // silently drops the spawn — never block gameplay on cosmetics.
      // Pass the projectile centre as `hitX/hitY` so directional-shield
      // enemies (`ShieldSatellite`) can reject front-half hits.
      const hitX = p.x + p.width / 2;
      const hitY = p.y + p.height / 2;
      const killed = e.takeDamage(1, hitX, hitY);
      if (killed) {
        spawnExplosion(e.x + e.width / 2, e.y + e.height / 2);
        maybeSpawnSpaceLoot(e.x + e.width / 2, e.y + e.height / 2);
      }
      p.dead = true;
      p.active = false;
      break;
    }
  }
}
